import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getLoggedInUser} from '../utils/functions';
import {UserInfo} from '../types/interfaces';



type UiState = 'idle' | 'submitting' | 'success' | 'error';

export const AdminCreateAgent: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [ui, setUi] = useState<UiState>('idle');
    const [msg, setMsg] = useState('');
    const [lastCreatedAccount, setLastCreatedAccount] = useState<string>('');
    const navigate = useNavigate();

    const API_BASE = process.env.REACT_APP_API_SELFCARE_URL as string;

    const [form, setForm] = useState({
        msisdn: '',
        clientName: '',
        contactName: '',
        contactPostalAddress: '',
        contactPhysicalAddress: '',
        contactTelephone: '',
        contactEmail: '',
        DocumentNumber: '',
        DocumentType: ''
    });

    useEffect(() => {
        const init = async () => {
            const u = await getLoggedInUser();
            setUserInfo(u);
            if (!u) navigate('/error');
        };
        init();
    }, [navigate]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target as HTMLInputElement;
        setForm(f => ({
            ...f,
            [name]: name === 'msisdn' ? value.replace(/\D/g, '').slice(0, 8) : value
        }));
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUi('submitting');
        setMsg('');

        // custom validation to avoid browser native tooltips
        if (!/^\d{8}$/.test(form.msisdn)) {
            setUi('error');
            setMsg('MSISDN must be exactly 8 digits (e.g. 73123456)');
            return;
        }

        for (const [k, v] of Object.entries(form)) {
            if (!v) {
                setUi('error');
                setMsg(`Field ${k} is required`);
                return;
            }
        }

        try {
            // Debug: confirm we're calling the proxy
            // eslint-disable-next-line no-console
            console.log('API_BASE', API_BASE);
            // eslint-disable-next-line no-console
            console.log('CREATE URL', `${API_BASE}/admin-ops/create-agent`);
            const res = await fetch(`${API_BASE}/admin-ops/create-agent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const raw = await res.text();
            let json: any = {};
            try { json = raw ? JSON.parse(raw) : {}; } catch (_e) { json = { message: raw }; }
            if (res.ok && json.created) {
                setUi('success');
                setMsg(`Account created: ${json.dealer?.accountNo || ''}`);
                // If created dealer account number is returned, navigate option to approve should be available
                const createdAccountNo = json.dealer?.accountNo || '';
                setForm({
                    msisdn: '', clientName: '', contactName: '', contactPostalAddress: '', contactPhysicalAddress: '', contactTelephone: '', contactEmail: '', DocumentNumber: '', DocumentType: ''
                });
                // attach the created account number to the message so UI can render an approve button
                if (createdAccountNo) {
                    // include in message for visual confirmation; actual approve button will use navigate with state
                    setMsg(`Account created: ${createdAccountNo}`);
                    // store the created account temporarily in local state so button can access it
                    // (we'll add local state above)
                    setLastCreatedAccount(createdAccountNo);
                }
            } else {
                setUi('error');
                setMsg(json.message || raw || 'Account not created');
            }
        } catch (err: any) {
            setUi('error');
            setMsg(`Network error: ${err.message}`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-green-800 mb-6">Merchant Account</h2>
                {ui !== 'idle' && (
                    <div className={`mb-6 p-4 rounded-lg text-sm ${ui === 'success' ? 'bg-green-50 border-l-4 border-green-500 text-green-700' : 'bg-red-50 border-l-4 border-red-500 text-red-700'}`}>
                        {msg}
                    </div>
                )}
                {/* If we just created an account, show Approve button that navigates to approve page with accountNo */}
                {lastCreatedAccount && (
                    <div className="mb-4 flex gap-3">
                        <button onClick={() => navigate('/admin/approve-merchant', { state: { accountNo: lastCreatedAccount } })} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md">Approve {lastCreatedAccount}</button>
                    </div>
                )}
                <form onSubmit={onSubmit} noValidate className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="msisdn" className="block text-sm font-medium text-green-700 mb-1">MSISDN</label>
                            <input 
                                id="msisdn"
                                name="msisdn" 
                                value={form.msisdn} 
                                onChange={onChange} 
                                placeholder="73123456"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                inputMode="numeric" 
                                maxLength={8} 
                                title="Enter 8 digits (e.g. 73123456)"
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="clientName" className="block text-sm font-medium text-green-700 mb-1">Client Name / Business Name</label>
                            <input 
                                id="clientName"
                                name="clientName" 
                                value={form.clientName} 
                                onChange={onChange} 
                                placeholder="Client Name / Business Name" 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="contactName" className="block text-sm font-medium text-green-700 mb-1">Contact Name</label>
                            <input 
                                id="contactName"
                                name="contactName" 
                                value={form.contactName} 
                                onChange={onChange} 
                                placeholder="Contact Name" 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="contactTelephone" className="block text-sm font-medium text-green-700 mb-1">Contact Telephone</label>
                            <input 
                                id="contactTelephone"
                                name="contactTelephone" 
                                value={form.contactTelephone} 
                                onChange={onChange} 
                                placeholder="Contact Telephone" 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="contactEmail" className="block text-sm font-medium text-green-700 mb-1">Contact Email</label>
                            <input 
                                id="contactEmail"
                                name="contactEmail" 
                                value={form.contactEmail} 
                                onChange={onChange} 
                                placeholder="Contact Email" 
                                type="email" 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="DocumentNumber" className="block text-sm font-medium text-green-700 mb-1">Document Number</label>
                            <input 
                                id="DocumentNumber"
                                name="DocumentNumber" 
                                value={form.DocumentNumber} 
                                onChange={onChange} 
                                placeholder="Document Number" 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label htmlFor="DocumentType" className="block text-sm font-medium text-green-700 mb-1">Document Type</label>
                            <select 
                                id="DocumentType"
                                name="DocumentType" 
                                value={form.DocumentType} 
                                onChange={onChange} 
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                                required
                            >
                                <option value="">Select Document Type</option>
                                <option value="NATIONAL_ID">NATIONAL_ID</option>
                                <option value="PASSPORT">PASSPORT</option>
                                <option value="BUSINESS">BUSINESS</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="contactPostalAddress" className="block text-sm font-medium text-green-700 mb-1">Postal Address</label>
                        <input 
                            id="contactPostalAddress"
                            name="contactPostalAddress" 
                            value={form.contactPostalAddress} 
                            onChange={onChange} 
                            placeholder="Postal Address" 
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                            required 
                        />
                    </div>
                    <div>
                        <label htmlFor="contactPhysicalAddress" className="block text-sm font-medium text-green-700 mb-1">Physical Address</label>
                        <input 
                            id="contactPhysicalAddress"
                            name="contactPhysicalAddress" 
                            value={form.contactPhysicalAddress} 
                            onChange={onChange} 
                            placeholder="Physical Address" 
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                            required 
                        />
                    </div>
                    <div className="flex gap-4 pt-4">
                        <button type="submit" disabled={ui === 'submitting'} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-md transition-colors">
                            {ui === 'submitting' ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </span>
                            ) : 'Merchant Account'}
                        </button>
                        <button type="button" onClick={() => navigate('/admin')} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-md transition-colors">Back</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminCreateAgent;

